import find from 'lodash/find'
import { createSerializer, useQueryStates } from 'nuqs'
import { useMemo } from 'react'

import { createQueryStatesKeys, processURLSearchParams } from '@Globals/Algolia/Shared'
import { PageProps } from '@Globals/Page/Types'
import { useTranslation } from '@Globals/Translations'

export interface PageCanonical {
  titleAfter: string[]
  titleBefore: string[]
  /** The canonical URL: the CMS url plus only the query parameters that have a canonical meaning. */
  url: string
  /** Query string (sorted, with the leading `?`) appended to the CMS url; empty when nothing canonical is in the query. */
  urlExtra: string
}

const NO_FACETS: never[] = []

/**
 * The page's canonical URL and the title parts that go with it. Only pagination (`page` above 1) and a single filter
 * listed in the pagination block's `canonicals` count; everything else in the address bar (sorting, tracking, extra
 * filters) is ignored, so `<link rel="canonical">`, `og:url` and the JSON-LD `ItemList` url all agree.
 *
 * The query is read and rendered through the list block's own nuqs key map, so `page` and `filter_*` are parsed
 * exactly as the block parses them.
 */
export const usePageCanonical = (
  page: { url?: string } | undefined,
  blockConfig: PageProps['blockConfig'] | undefined,
): PageCanonical => {
  const { t } = useTranslation()
  const algolia = find(blockConfig, block => block.algolia?.usePagination === true)?.algolia
  const keyMap = useMemo(() => createQueryStatesKeys(algolia?.facetParameters ?? NO_FACETS), [algolia?.facetParameters])
  const [states] = useQueryStates(keyMap)
  const serialize = useMemo(() => createSerializer(keyMap, { processUrlSearchParams: processURLSearchParams }), [keyMap])

  return useMemo(() => {
    const pageURL = page?.url || ''
    const canonicals = algolia?.canonicals
    if (!canonicals) {
      return { url: pageURL, urlExtra: '', titleAfter: [], titleBefore: [] }
    }

    const titleAfter: string[] = []
    const titleBefore: string[] = []
    const canonical: Record<string, unknown> = {}

    if (states.page > 1) {
      titleAfter.push(t('PAGINATION_PAGE_HEADER', { count: states.page }))
      canonical.page = states.page
    }

    const activeFilters = Object.keys(states).filter(key => key.startsWith('filter_') && states[key] !== null)
    if (activeFilters.length === 1) {
      const [filterKey] = activeFilters
      const values = states[filterKey]
      if (Array.isArray(values) && values.length === 1) {
        const name = canonicals[`${filterKey}=${values[0]}`]?.name?.defaults
        if (name) {
          titleBefore.push(t(name))
          canonical[filterKey] = values
        }
      }
    }

    const urlExtra = serialize(canonical)
    return { url: `${pageURL}${urlExtra}`, urlExtra, titleAfter, titleBefore }
  }, [algolia?.canonicals, page?.url, serialize, states, t])
}
